import * as Yup from "yup";
import { Field, Form, Formik } from "formik";
import { useCallback, useContext, useMemo, useState } from "react";

import { observer } from "mobx-react-lite";
import { StoreContext } from "../../store";
import useTranslation from "next-translate/useTranslation";
import {
  Box,
  Button,
  FormHelperText,
  InputLabel,
  Link,
  Radio,
  RadioGroup,
  FormControlLabel,
  Typography,
  IconButton,
  InputAdornment,
} from "@material-ui/core";
import {
  Section,
  SelectField,
  SubmitButton,
  TextField,
} from "@microrealestate/commonui/components";
import { toJS } from "mobx";
import { CustomSwitchField } from "@microrealestate/commonui/components/FormFields/SwitchField";
import { SettingGridRow } from "../GridRow";
import { Cancel, Error, VerifiedUser, VpnKey } from "@material-ui/icons";
import { getChangedFields, getChangesWithFlag } from "../../utils/logic/data.comparison";

const PaymentSettings = observer(({ onSubmit }) => {
  const { t } = useTranslation("common");
  const store = useContext(StoreContext);
  const [midtransValidation, setMidtransValidation] = useState(false);
  const [xenditValidation, setXenditValidation] = useState(false);
  const [faspayValidation, setFaspayValidation] = useState(false);

  const paymentGateways = [
    { id: "none", name: "-", value: null },
    { id: "midtrans", name: "Midtrans", value: "midtrans" },
    { id: "xendit", name: "Xendit", value: "xendit" },
    { id: "faspay", name: "Faspay", value: "faspay" },
  ];

  const onValidateKey = async ({ provider, apiKey, faspayMerchantId, faspayUserId, faspayPassword }) => {
    if (provider === "faspay") {
      if (!faspayMerchantId?.trim() || !faspayUserId?.trim() || !faspayPassword?.trim()) {
        return store.pushToastMessage({
          message: t("Missing Faspay credentials"),
          severity: "warning",
        });
      }
    } else {
      if (!apiKey || !apiKey.trim()) {
        return store.pushToastMessage({
          message: t("Missing Api Key"),
          severity: "warning",
        });
      }
    }

    try {
      let response;
      switch (provider) {
        case "midtrans": {
          const credentials = btoa(`${apiKey}:`);
          response = await store.organization.validation({ apiKey: credentials });
          break;
        }
        case "xendit": {
          const credentials = btoa(`${apiKey}:`);
          response = await store.organization.validationXendit({ apiKey: credentials });
          break;
        }
        case "faspay":
          response = await store.organization.validationFaspay({
            merchantId: faspayMerchantId,
            userId: faspayUserId,
            password: faspayPassword,
          });
          break;
      }

      if (response.status == 204 || response.status == 202 || response.status == 200) {
        if (provider === "midtrans") {
          setMidtransValidation(true);
          setXenditValidation(false);
          setFaspayValidation(false);
        } else if (provider === "xendit") {
          setMidtransValidation(false);
          setXenditValidation(true);
          setFaspayValidation(false);
        } else if (provider === "faspay") {
          setMidtransValidation(false);
          setXenditValidation(false);
          setFaspayValidation(true);
        }
        return store.pushToastMessage({
          message: t("Validation success"),
          severity: "success",
        });
      } else {
        setMidtransValidation(false);
        setXenditValidation(false);
        setFaspayValidation(false);
        return store.pushToastMessage({
          message: t("Validation failed"),
          severity: "error",
        });
      }
    } catch (error) {
      setMidtransValidation(false);
      setXenditValidation(false);
      setFaspayValidation(false);
      console.error(error);
    }
  };

  const initialValues = useMemo(() => {
    const selectedOrg = toJS(store.organization.selected);
    const { paymentSettings } = selectedOrg;
    return {
      // V2 Schema
      allowedMethods: paymentSettings.allowedMethods,
      onlineGateway: {
        enabled: paymentSettings.onlineGateway.enabled || false,
        provider: paymentSettings.onlineGateway.provider || null,
        apiKey: paymentSettings.onlineGateway.apiKey || "",
        faspayMerchantId: paymentSettings.onlineGateway.faspayMerchantId || "",
        faspayUserId: paymentSettings.onlineGateway.faspayUserId || "",
        faspayPassword: paymentSettings.onlineGateway.faspayPassword || "",
      },

      // V1 Schema - deprecated
      // paymentMethod: {
      //   midtrans: selectedOrg.paymentMethod?.midtrans || "",
      //   xendit: selectedOrg.paymentMethod?.xendit || "",
      // },
      // selectedMethod: selectedOrg.paymentMethod?.midtrans
      //   ? "midtrans"
      //   : selectedOrg.paymentMethod?.xendit
      //   ? "xendit"
      //   : "",
    };
  }, [store.organization.selected]);

  const _onSubmit = useCallback(
    async (settings) => {
      const { allowedMethods, onlineGateway } = settings;
      if (onlineGateway.enabled) {
        if (onlineGateway.provider === "midtrans" && !midtransValidation) {
          return store.pushToastMessage({
            message: t("Please validate Midtrans API Key first"),
            severity: "error",
          });
        }
        if (onlineGateway.provider === "xendit" && !xenditValidation) {
          return store.pushToastMessage({
            message: t("Please validate Xendit API Key first"),
            severity: "error",
          });
        }
        if (onlineGateway.provider === "faspay" && !faspayValidation) {
          return store.pushToastMessage({
            message: t("Please validate Faspay credentials first"),
            severity: "error",
          });
        }
        if (!onlineGateway.provider) {
          return store.pushToastMessage({
            message: t("Please select provider"),
            severity: "error",
          });
        }
      }

      const changedFields = getChangesWithFlag(settings, initialValues);
      const payload = {
        // TODO: use latest schema
        // v2 schema
        isPaymentSettingUpdated: changedFields.changed,
        paymentSettings: settings,

        // TODO: remove deprecated schema values
        // deprecated schema
        // paymentMethod: !onlineGateway.enabled
        //   ? {
        //       midtrans: null,
        //       xendit: null,
        //     }
        //   : {
        //       midtransUpdated:
        //         (onlineGateway.provider === "midtrans" &&
        //           onlineGateway.apiKey !== initialValues.onlineGateway.apiKey) ||
        //         false,
        //       xenditUpdated:
        //         (onlineGateway.provider === "xendit" &&
        //           onlineGateway.apiKey !== initialValues.onlineGateway.apiKey) ||
        //         false,
        //       midtrans:
        //         (onlineGateway.provider === "midtrans" ? onlineGateway.apiKey : null) || null,
        //       xendit: (onlineGateway.provider === "xendit" ? onlineGateway.apiKey : null) || null,
        //     },
      };
      // console.log(payload);
      await onSubmit(payload);
    },
    [onSubmit, midtransValidation, xenditValidation, faspayValidation]
  );

  return (
    <Formik initialValues={initialValues} onSubmit={_onSubmit}>
      {({ values, setFieldValue, isSubmitting }) => {
        const selectedProvider = values.onlineGateway.provider;
        let providerUrl, providerLabel;
        switch (selectedProvider) {
          case "midtrans":
            providerUrl = "https://superkos.id/docs/cara-setting-pembayaran-melalui-midtrans/";
            providerLabel = "Midtrans";
            break;
          case "xendit":
            providerUrl = "https://superkos.id/docs/cara-setting-pembayaran-melalui-xendit/";
            providerLabel = "Xendit";
            break;
          case "faspay":
            providerUrl = "https://superkos.id/docs/cara-setting-pembayaran-melalui-faspay/";
            providerLabel = "Faspay";
            break;
          default:
            providerUrl = null;
            providerLabel = null;
            break;
        }

        return (
          <Form>
            <Section label={t("Payment Settings")}>
              <Box display="flex" alignItems="flex-start" mb={1.5}>
                <CustomSwitchField name={`onlineGateway.enabled`} color="primary" />
                <Box display="flex" flexDirection="column" ml={1} width="100%">
                  <Typography variant="subtitle1">{t("Online Gateway")}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {t("Determines whether online payment gateways are available for payments")}
                  </Typography>
                  <SettingGridRow
                    left={
                      <Box
                        display="grid"
                        gridTemplateColumns="180px 1fr"
                        gridGap={24}
                        // columnGap={12} // explicit gap (theme.spacing(2))
                        alignItems="flex-start"
                      >
                        <SelectField
                          label={t("Provider")}
                          name="onlineGateway.provider"
                          values={paymentGateways.map(({ id, value, name }) => ({
                            id,
                            value,
                            label: t(name),
                          }))}
                          disabled={!values.onlineGateway.enabled}
                        />

                        {values.onlineGateway.provider && values.onlineGateway.provider !== "faspay" && (
                          <TextField
                            label={t("API Key")}
                            type="password"
                            name="onlineGateway.apiKey"
                            showHidePassword={false}
                            disabled={!values.onlineGateway.enabled}
                            endAdornment={
                              values.onlineGateway.enabled ? (
                                <InputAdornment position="end">
                                  {!!values.onlineGateway.apiKey ? (
                                    midtransValidation || xenditValidation ? (
                                      <VerifiedUser style={{ color: "#38b000" }} />
                                    ) : (
                                      <Cancel style={{ color: "#d00000" }} />
                                    )
                                  ) : (
                                    <Error style={{ color: "#ffb703" }} />
                                  )}
                                </InputAdornment>
                              ) : null
                            }
                          />
                        )}
                        {values.onlineGateway.provider === "faspay" && (
                          <>
                            <TextField
                              label={t("Merchant ID")}
                              name="onlineGateway.faspayMerchantId"
                              disabled={!values.onlineGateway.enabled}
                            />
                            <TextField
                              label={t("User ID")}
                              name="onlineGateway.faspayUserId"
                              disabled={!values.onlineGateway.enabled}
                              endAdornment={
                                values.onlineGateway.enabled ? (
                                  <InputAdornment position="end">
                                    {!!values.onlineGateway.faspayMerchantId && !!values.onlineGateway.faspayUserId && !!values.onlineGateway.faspayPassword ? (
                                      faspayValidation ? (
                                        <VerifiedUser style={{ color: "#38b000" }} />
                                      ) : (
                                        <Cancel style={{ color: "#d00000" }} />
                                      )
                                    ) : (
                                      <Error style={{ color: "#ffb703" }} />
                                    )}
                                  </InputAdornment>
                                ) : null
                              }
                            />
                            <TextField
                              label={t("Merchant Password")}
                              type="password"
                              name="onlineGateway.faspayPassword"
                              showHidePassword={false}
                              disabled={!values.onlineGateway.enabled}
                            />
                          </>
                        )}
                      </Box>
                    }
                    right={
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-end"
                        height="100%"
                      >
                        <Button
                          component="span"
                          variant="contained"
                          disabled={!values.onlineGateway.enabled || !values.onlineGateway.provider}
                          onClick={() => onValidateKey(values.onlineGateway)}
                        >
                          {t("Validate")}
                        </Button>
                      </Box>
                    }
                  />
                  {values.onlineGateway.provider && (
                    <Typography variant="subtitle2">
                      <Link
                        href={providerUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        color="initial"
                        underline="none"
                      >
                        Klik di sini untuk panduan setting akun {providerLabel}
                      </Link>
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* <InputLabel shrink>*Saat ini hanya bisa memakai satu metode pembayaran</InputLabel>
              <Box marginBottom="30px">
                <RadioGroup
                  aria-label="payment method"
                  name="selectedMethod"
                  value={values.selectedMethod}
                  onChange={(event) => {
                    const method = event.target.value;
                    setFieldValue("selectedMethod", method);
                  }}
                >
                  <FormControlLabel value="midtrans" control={<Radio />} label="Midtrans" />
                  <Box margin="0 0 15px 30px">
                    <TextField
                      label={t("Midtrans API Key:")}
                      type="password"
                      name="paymentMethod.midtrans"
                      showHidePassword={false}
                      value={values.paymentMethod.midtrans}
                      onChange={(e) => setFieldValue("paymentMethod.midtrans", e.target.value)}
                      disabled={values.selectedMethod !== "midtrans"}
                    />
                    <Box display="flex" alignItems="center">
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => onValidationMidtrans(values.paymentMethod.midtrans)}
                        disabled={!values.paymentMethod.midtrans}
                      >
                        {t("Validation")}
                      </Button>
                      <Box ml={1}>
                        <Link
                          href="https://superkos.id/docs/cara-setting-pembayaran-melalui-midtrans/"
                          target="_blank"
                          rel="noopener noreferrer"
                          color="blue"
                          underline="none"
                        >
                          Klik di sini untuk panduan setting akun Midtrans
                        </Link>
                      </Box>
                    </Box>
                    {midtransApiKeyError && (
                      <FormHelperText error>{t("This field is required")}</FormHelperText>
                    )}
                  </Box>

                  <FormControlLabel value="xendit" control={<Radio />} label="Xendit" />
                  <Box margin="0 0 0 30px">
                    <TextField
                      label={t("Xendit API Key:")}
                      type="password"
                      name="paymentMethod.xendit"
                      showHidePassword={false}
                      value={values.paymentMethod.xendit}
                      onChange={(e) => setFieldValue("paymentMethod.xendit", e.target.value)}
                      disabled={values.selectedMethod !== "xendit"}
                    />
                    <Box display="flex" alignItems="center">
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => onValidationXendit(values.paymentMethod.xendit)}
                        disabled={!values.paymentMethod.xendit}
                      >
                        {t("Validation")}
                      </Button>
                      <Box ml={1}>
                        <Link
                          href="https://superkos.id/docs/cara-setting-pembayaran-melalui-xendit/"
                          target="_blank"
                          rel="noopener noreferrer"
                          color="blue"
                          underline="none"
                        >
                          Klik di sini untuk panduan setting akun Xendit
                        </Link>
                      </Box>
                    </Box>
                    {xenditApiKeyError && (
                      <FormHelperText error>{t("This field is required")}</FormHelperText>
                    )}
                  </Box>
                </RadioGroup>
              </Box> */}
            </Section>
            <SubmitButton size="large" label={!isSubmitting ? t("Save") : t("Saving")} />
          </Form>
        );
      }}
    </Formik>
  );
});

// const InvoiceReminderConfig = ({ t, values = {}, disabled }) => {
//   const classes = useStyles();
//   const reminderConfigs = [
//     {
//       key: "preReminder",
//       title: "Faktur",
//       description: "Will be sent x days before due",
//     },
//     {
//       key: "firstReminder",
//       title: "First reminder",
//       description: "Will be sent x days after due",
//     },
//     {
//       key: "secondReminder",
//       title: "Second reminder",
//       description: "Will be sent x days after due",
//     },
//     {
//       key: "lastReminder",
//       title: "Last reminder",
//       description: "Will be sent x days after due",
//     },
//   ];

//   return (
//     <Box display="flex" flexDirection="column" mb={1}>
//       <Box display="flex" alignItems="flex-start" mb={1.5}>
//         <CustomSwitchField name="invoiceReminder.enabled" />

//         <Box ml={1} width="100%">
//           <Typography variant="subtitle1">{t("Invoice Reminder")}</Typography>
//           <Typography variant="caption" color="textSecondary">
//             {t("Automatically remind tenants about upcoming or overdue invoices")}
//           </Typography>

//           <Box mt={1}>
//             {reminderConfigs.map(({ key, title, description }) => {
//               const prefix = `invoiceReminder.${key}`;
//               const isEnabled = values.invoiceReminder[key].enabled;
//               return (
//                 <SettingGridRow
//                   key={key}
//                   disabled={disabled}
//                   left={
//                     <Box display="flex" alignItems="flex-start">
//                       <CustomSwitchField
//                         color="primary"
//                         name={`${prefix}.enabled`}
//                         disabled={disabled}
//                       />

//                       <Box ml={1}>
//                         <Typography variant="subtitle1">{t(title)}</Typography>
//                         <Typography variant="caption" color="textSecondary">
//                           {t(description)}
//                         </Typography>
//                       </Box>
//                     </Box>
//                   }
//                   // right={

//                   // }
//                 />
//               );
//             })}
//           </Box>
//         </Box>
//       </Box>
//     </Box>
//   );
// };

export default PaymentSettings;
